import {
  AccountUser,
  ClubDocument,
  type ClubDocumentAcceptance,
  type ClubDocumentAudience,
} from "@mixitone/models";
import { FormController } from "components/Forms/FormController";
import { type FormFieldConfig, selectField } from "components/Forms/FormFieldsDefinition";
import { saveClubDocumentWithFile } from "lib/documentFormSave";

interface Props {
  clubId: string;
  onSave?: (document: ClubDocument) => void;
}

type Attributes = Partial<ClubDocument>;

class DocumentNewModalController extends FormController<ClubDocument, Attributes, object, Props> {
  static override initialState = {
    ...FormController.initialState,
  };

  override buildRecord(): Promise<ClubDocument> {
    return Promise.resolve(
      new ClubDocument({
        club_id: this.props.clubId,
        version: 1,
        audience: "club",
        acceptance: "none",
        file_storage: "public_images",
      }),
    );
  }

  override async saveRecord(record: ClubDocument) {
    record.uploaded_by_id = AccountUser.current!.id!;
    await saveClubDocumentWithFile(record);
  }

  actionSetDocumentId(documentId: string) {
    this.state.record!.document_id = documentId;
    this.state.record!.file_storage = "private_documents";
  }

  fields(): Record<string, FormFieldConfig> {
    return {
      name: {
        type: "text",
        label: "Document name",
        name: "name",
        required: true,
      },
      document_id: {
        type: "upload",
        label: "Document file",
        variant: "pdf",
        name: "document_id",
        required: true,
      },
      audience: selectField<ClubDocumentAudience>({
        label: "Who can see this",
        name: "audience",
        hidePlaceholderOption: true,
        helpText:
          "Admins is club staff. Members is current members and pending invites. Whole club is anyone logged in at this club.",
        options: [
          { label: "Admins", value: "admins" },
          { label: "Members", value: "members" },
          { label: "Whole club", value: "club" },
        ],
        value: () => this.state.record?.audience ?? "club",
        onChange: (value) => {
          const record = this.state.record;
          if (!record) return;
          record.audience = value;
          if (value !== "members") record.acceptance = "none";
        },
      }),
      acceptance: selectField<ClubDocumentAcceptance>({
        label: "Who must accept",
        name: "acceptance",
        hidePlaceholderOption: true,
        condition: (record) => record?.audience === "members",
        helpText:
          "None means members can read it without agreeing. New members agree before they join. All current members must agree to this version.",
        options: [
          { label: "None", value: "none" },
          { label: "New members", value: "new_members" },
          { label: "All current members", value: "all_current_members" },
        ],
        value: () => this.state.record?.acceptance ?? "none",
        onChange: (value) => {
          if (this.state.record) this.state.record.acceptance = value;
        },
      }),
    } as const satisfies Record<string, FormFieldConfig>;
  }
}

export { DocumentNewModalController };
